import { z } from 'zod';

// Base entities
export const ProjectSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const BucketSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  project_id: z.number(),
  created_at: z.string(),
});

export const FileRecordSchema = z.object({
  id: z.number(),
  filename: z.string(),
  size: z.number(),
  mime_type: z.string().nullable(),
  blob_hash: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  folder_id: z.number().optional(),
});

// Upload types
export const UploadStrategySchema = z.enum([
  'direct',
  'multipart', 
  'chunked_fast',
  'chunked_queued',
  'streaming_distributed'
]);

export const UploadStatusSchema = z.enum([
  'initiated',
  'uploading',
  'processing', 
  'completed',
  'failed',
  'cancelled',
  'paused'
]);

export const UploadSessionSchema = z.object({
  upload_id: z.string(),
  chunk_size: z.number(),
  expires_at: z.string(),
  upload_url: z.string(),
  strategy: UploadStrategySchema,
  max_file_size: z.number(),
  supported_features: z.array(z.string()),
});

export const UploadProgressSchema = z.object({
  upload_id: z.string(),
  uploaded_bytes: z.number(),
  total_bytes: z.number(),
  completed: z.boolean(),
  progress_percentage: z.number(),
  file_id: z.number().optional(),
  next_chunk_index: z.number().optional(),
  estimated_time_remaining: z.string().optional(),
});

export const UploadStatusDetailsSchema = z.object({
  upload_id: z.string(),
  status: UploadStatusSchema,
  uploaded_bytes: z.number(),
  total_bytes: z.number(),
  progress_percentage: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  estimated_time_remaining: z.string().optional(),
  current_throughput_mbps: z.number().optional(),
  error_count: z.number(),
  last_error: z.string().optional(),
});

// Authentication types
export const AuthUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  is_admin: z.boolean(),
  avatar_url: z.string().optional(),
});

export const LoginResponseSchema = z.object({
  csrf_token: z.string(),
  user_id: z.string(),
  is_admin: z.boolean(),
  totp_enabled: z.boolean(),
});

export const SessionCheckSchema = z.object({
  authenticated: z.boolean(),
  user: AuthUserSchema.optional(),
});

// API request types
export const InitiateUploadRequestSchema = z.object({
  filename: z.string(),
  bucket_id: z.number(),
  total_size: z.number(),
  folder_id: z.number().optional(),
  mime_type: z.string().optional(),
  priority: z.enum(['low', 'normal', 'high', 'critical']).optional(),
});

export const CreateProjectRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
});

export const CreateBucketRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  project_id: z.number(),
});

// Export TypeScript types
export type Project = z.infer<typeof ProjectSchema>;
export type Bucket = z.infer<typeof BucketSchema>;
export type FileRecord = z.infer<typeof FileRecordSchema>;
export type UploadStrategy = z.infer<typeof UploadStrategySchema>;
export type UploadStatus = z.infer<typeof UploadStatusSchema>;
export type UploadSession = z.infer<typeof UploadSessionSchema>;
export type UploadProgress = z.infer<typeof UploadProgressSchema>;
export type UploadStatusDetails = z.infer<typeof UploadStatusDetailsSchema>;
export type AuthUser = z.infer<typeof AuthUserSchema>;
export type LoginResponse = z.infer<typeof LoginResponseSchema>;
export type SessionCheck = z.infer<typeof SessionCheckSchema>;
export type InitiateUploadRequest = z.infer<typeof InitiateUploadRequestSchema>;
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;
export type CreateBucketRequest = z.infer<typeof CreateBucketRequestSchema>;

// Pagination types
export const PaginatedResponseSchema = <T>(itemSchema: z.ZodType<T>) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    total_pages: z.number(),
  });

export type PaginatedResponse<T> = {
  items: T[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
};

// Common API response wrapper
export const ApiResponseSchema = <T>(dataSchema: z.ZodType<T>) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
    message: z.string().optional(),
    request_id: z.string().optional(),
    timestamp: z.string().optional(),
  });

export type ApiResponse<T> = {
  success: boolean;
  data: T;
  message?: string;
  request_id?: string;
  timestamp?: string;
};

// Re-export storage types
export type { StorageUsage, ProjectStorage, BucketStorage, SystemStorageStats, StorageStatus } from './modules/storage';